import React, { useCallback, useMemo } from "react";
import { useState, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import AttendanceCard from "../components/AttendanceCard"
import AttendanceTrendChart from "../components/AttendanceTrendChart"
import SkSelect from "../components/SkSelect"
import DateRangeFilter from "../components/DateRangeFilter";
import { exportToPDF } from "../utils/pdfExport";
import ReportTable from "../components/ReportTable";
import Loader from "../components/Loader";
import Pagination from "../components/Pagination";
import DownloadProgressModal from "../components/DownloadProgressModal";
import "../assets/css/attendance.css"
import { getAttendancList, generateAttendance, getDashboard } from "../redux/attendance/attendanceAction";
import { selectAttendanceList, selectAttendanceLoading, selectAttendanceMeta, selectAttendanceDashboard }
    from "../redux/attendance/attendanceSelectors";
import { attendanceStatusList } from "../redux/status/StatusActions";
import { selectAttendanceStatus } from "../redux/status/StatusSelector";
import moment from "moment-timezone"
import { formatWithTimeZone, formatTime12Hour } from "../utils/common";

const STATUS_FILTER_OPTIONS = [
    { value: "", label: "All Status" },
    { value: "Present", label: "Present" },
    { value: "On Duty", label: "On Duty" },
    { value: "Absent", label: "Absent" },
];

const Attendance = () => {
    const [dateRange, setDateRange] = useState({
        start: new Date().toISOString().split("T")[0],
        end: new Date().toISOString().split("T")[0],
    });
    const dispatch = useDispatch();
    const attendanceList = useSelector(selectAttendanceList);
    const dashboard = useSelector(selectAttendanceDashboard);
    const loading = useSelector(selectAttendanceLoading);
    const attendanceMeta = useSelector(selectAttendanceMeta);
    const attendanceStatuses = useSelector(selectAttendanceStatus);

    const [finalExportData, setFinalExportData] = useState(null);
    const [visibleColumns, setVisibleColumns] = useState([]);
    const [tableFilters, setTableFilters] = useState([]);
    const [isDownloading, setIsDownloading] = useState(false);
    const [downloadProgress, setDownloadProgress] = useState(0);
    const [trendDays, setTrendDays] = useState(7);
    const [statusFilter, setStatusFilter] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(5);
    const [filters, setFilters] = useState({
        ApprovalStatus: "",
        UserName: "",
        AttendanceDate: "",
        ShiftName: "",
        ShiftGroupName: "",
        ClockIn: "",
        ClockOut: "",
        WorkingTime: "",
        MotHours: "",
        Status: "",
        IsManual: "",
    });
    const [debouncedFilters, setDebouncedFilters] = useState(filters);
    const timeoutRef = useRef(null);
    const isMounted = useRef(true);
    const handleTableDataChange = useCallback((data) => {
        setFinalExportData((prev) => {
            if (prev === data) return prev;
            if (prev && data && prev.length === data.length && prev[0] === data[0]) return prev;
            return data;
        });
    }, []);
    const handleVisibleColumnsChange = useCallback((cols) => {
        setVisibleColumns(cols);
    }, []);

    const handleTableFilterChange = useCallback((filters) => {
        setTableFilters(filters);
    }, []);

    const handleDateChange = (start, end) => {
        const formatDate = (d) =>
            d ? new Date(d).toISOString().split("T")[0] : null;
        setDateRange({ start: formatDate(start), end: formatDate(end) });
    };

    const handleExportClick = async () => {
        const dataToExport =
            finalExportData !== null
                ? finalExportData
                : attendanceData;
        const columnsToExport =
            visibleColumns.length > 0
                ? visibleColumns
                : columns;
        if (dataToExport.length === 0) {
            alert("No data available to export.");
            return;
        }
        setIsDownloading(true);
        setDownloadProgress(0);
        for (let i = 0; i <= 90; i += 10) {
            if (!isMounted.current) return;
            setDownloadProgress(i);
            await new Promise(resolve => setTimeout(resolve, 150));
        }

        const liveWidths = {};
        const tableEl = document.querySelector(".report-container table");
        if (tableEl) {
            const thElements = tableEl.querySelectorAll("th");
            columnsToExport.forEach((col, index) => {
                if (thElements[index]) {
                    liveWidths[col.key] =
                        thElements[index].getBoundingClientRect().width;
                }
            });
        }
        const activeFiltersList = [];
        tableFilters.forEach((f) => {
            if (f.value) {
                const col = columns.find(c => c.key === f.column);
                activeFiltersList.push(
                    `${col.label}: ${f.value}`
                );
            }
        });
        exportToPDF(
            dataToExport,
            columnsToExport,
            "Attendance Report",
            {
                startDate: new Date(dateRange.start),
                endDate: new Date(dateRange.end),
            },
            null,
            liveWidths,
            activeFiltersList
        );
        setDownloadProgress(100);
        timeoutRef.current = setTimeout(() => {
            if (!isMounted.current) return;
            setIsDownloading(false);
            setDownloadProgress(0);
        }, 800);
    };
    const buildParams = useCallback(() => {
        let statusPayload = {};

        switch (filters.ApprovalStatus) {

            case "Approved":
                statusPayload = {
                    Approval: 1,
                };
                break;

            case "Rejected":
                statusPayload = {
                    Reject: 1,
                };
                break;

            case "":
                statusPayload = {};
                break;

            default:
                statusPayload = {
                    Pending: 1,
                };
        }
        const payload = {
            PageNumber: currentPage,
            PageSize: pageSize,
            FromDate: dateRange.start,
            ToDate: dateRange.end,
            ...statusPayload,
        };


        if (filters.UserName)
            payload.UserName = filters.UserName;

        if (filters.AttendanceDate)
            payload.AttendanceDate = moment(filters.AttendanceDate).format("YYYY-MM-DD");

        if (filters.ShiftName)
            payload.ShiftName = filters.ShiftName;

        if (filters.ShiftGroupName)
            payload.ShiftGroupName = filters.ShiftGroupName;

        if (filters.ClockIn)
            payload.ClockIn = filters.ClockIn;

        if (filters.ClockOut)
            payload.ClockOut = filters.ClockOut;

        if (filters.WorkingTime)
            payload.WorkingTime = filters.WorkingTime;

        if (filters.MotHours)
            payload.MotHours = filters.MotHours;

        if (filters.Status) {
            payload.Status = filters.Status;
        }
        if (filters.IsManual)
            payload.IsManual = filters.IsManual;
        return payload;
    }, [currentPage, pageSize, dateRange, filters]);
    useEffect(() => {
        dispatch(attendanceStatusList({}));
    }, [dispatch]);
    useEffect(() => {
        dispatch(getAttendancList(buildParams()));

        dispatch(
            getDashboard({
                FromDate: dateRange.start,
                ToDate: dateRange.end,
            })
        );
    }, [
        currentPage,
        pageSize,
        dateRange.start,
        dateRange.end,
        debouncedFilters,
        dispatch,
    ]);

    const handleHeaderFilterChange = useCallback((cf) => {

        const newFilters = {

            UserName: cf.userName?.value || "",
            AttendanceDate: cf.attendanceDate?.start || "",
            RowNo: cf.sno?.value || "",
            ShiftName: cf.shiftName?.value || "",
            ShiftGroupName: cf.shiftGroupName?.value || "",
            ClockIn: cf.clockIn?.value || "",
            ClockOut: cf.clockOut?.value || "",
            WorkingTime: cf.workingTime?.value || "",
            MotHours: cf.motHours?.value || "",
            Status: cf.statusLabel?.value || "",
            IsManual: cf.isManual?.value || "",
        };

        setFilters((prev) => {
            if (JSON.stringify(prev) === JSON.stringify(newFilters)) {
                return prev;
            }

            return newFilters;
        });

        setCurrentPage(1);

    }, []);
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedFilters(filters);
        }, 700);

        return () => clearTimeout(timer);
    }, [filters]);
    const attendanceData = useMemo(() => {
        return (attendanceList || []).map((item) => ({
            id: item.AttendanceCode,
            approvalStatus: (
                <span
                    className={`approval-status ${item.ApprovalStatus === "Pending"
                        ? "pending"
                        : item.ApprovalStatus === "Approved"
                            ? "approved"
                            : item.ApprovalStatus === "Rejected"
                                ? "rejected" : ""
                        }`} >
                    {item.ApprovalStatus || "-"}
                </span>),
            sno: item.RowNo || "-",
            attendanceDate: formatWithTimeZone(item.AttendanceDate, "DD-MM-yy"),
            userName: item.UserName?.toUpperCase() || "-",
            shiftName: item.ShiftName?.toUpperCase() || "-",
            shiftGroupName: item.ShiftGroupName?.toUpperCase() || "-",
            clockIn: formatWithTimeZone(item.ClockIn, "hh:mm A"),
            clockOut: formatWithTimeZone(item.ClockOut, "hh:mm A"),
            workingTime: item.TotalHours
                ? `${moment.utc(item.TotalHours).format("HH")}h:${moment
                    .utc(item.TotalHours)
                    .format("mm")}m`
                : "-",
            motHours: item.MOTHours
                ? `${moment.utc(item.MOTHours).format("HH")}h:${moment
                    .utc(item.MOTHours).format("mm")}m`
                : "-",
            status: item.StatusCode || "",
            statusLabel: item.StatusCode  || "-",
            isManual: item.IsManual ? "Yes" : "No",
        }));
    }, [attendanceList]);
    const statusOptions = useMemo(() => [
        { value: "", label: "All" },

        ...(attendanceStatuses || []).filter((status) => status.Status === true || status.Status === 1).map(item => ({
            value: item.ShortName,
            label: (item.AttendanceStatus).toUpperCase(),
        }))
    ], [attendanceStatuses]);
    const columns = useMemo(() => [
        // {
        //     label: "Approval Status", key: "approvalStatus", width: "150px", filterType: "select", filterOptions: [
        //         { value: "", label: "All" },
        //         { value: "Pending", label: "Pending" },
        //         { value: "Approved", label: "Approved" },
        //         { value: "Rejected", label: "Rejected" },
        //     ],
        // },
        { label: "S NO", key: "sno", width: "100px", filterType: "text", },
        { label: "Employee Name", key: "userName", width: "200px", filterType: "text", },
        { label: "Attendance Date", key: "attendanceDate", width: "150px", filterType: "date", },
        { label: "Shift Name", key: "shiftName", width: "180px", filterType: "text", },
        { label: "Shift Group", key: "shiftGroupName", width: "180px", filterType: "text", },
        { label: "Clock In", key: "clockIn", width: "170px", filterType: "time", },
        { label: "Clock Out", key: "clockOut", width: "170px", filterType: "time", },
        { label: "Total Time", key: "workingTime", width: "170px", filterType: "text", },
        { label: "OT Hours", key: "motHours", width: "140px", filterType: "text", },
        {
            label: "Status",
            key: "statusLabel",
            width: "120px",
            filterType: "select",
            filterOptions: statusOptions,
        },
        {
            label: "Is Manual", key: "isManual", width: "120px", filterType: "select",
            filterOptions: [
                { label: "All", value: "" },
                { label: "Yes", value: "1" },
                { label: "No", value: "0" },
            ],
        },
    ], [statusOptions]);

    const handleGenerate = () => {
        const payload = {
            FromDate: dateRange.start,
            ToDate: dateRange.end,
        };

        dispatch(generateAttendance(payload)).then((res) => {
            if (res.meta.requestStatus === "fulfilled") {
                dispatch(getAttendancList(buildParams()));

                dispatch(
                    getDashboard({
                        FromDate: dateRange.start,
                        ToDate: dateRange.end,
                    })
                );
            }
        });
    };

    // The list endpoint is not paginated, so page the loaded rows client-side.
    // const pagedAttendanceData = useMemo(
    //     () =>
    //         attendanceData.slice(
    //             (currentPage - 1) * pageSize,
    //             currentPage * pageSize
    //         ),
    //     [attendanceData, currentPage, pageSize]
    // );

    const present = dashboard?.cardMap?.Present;
    const onDuty = dashboard?.cardMap?.OnDuty;
    const absent = dashboard?.cardMap?.Absent;
    const leave = dashboard?.cardMap?.Leave;

    // There is no weekly-trend endpoint, so the 7-day trend is derived from the
    // attendance rows: bucket by AttendanceDate, then count per status.
    // Status labels are user-defined in the Status master, so match them loosely.
    const attendanceTrend = useMemo(() => {
        const categoryOf = (row) => {
            const label = String(
                row?.StatusLabel || row?.StatusCode || ""
            ).toLowerCase();
            if (label.includes("present")) return "Present";
            if (label.includes("duty")) return "OnDuty";
            if (label.includes("leave")) return "Leave";
            if (label.includes("absent")) return "Absent";
            return null;
        };

        const end = dateRange.end ? moment(dateRange.end) : moment();
        const days = [];
        for (let i = trendDays - 1; i >= 0; i--) {
            days.push(end.clone().subtract(i, "days"));
        }

        const buckets = {};
        days.forEach((d) => {
            buckets[d.format("YYYY-MM-DD")] = {
                Present: 0,
                OnDuty: 0,
                Leave: 0,
                Absent: 0,
            };
        });

        (attendanceList || []).forEach((row) => {
            if (!row?.AttendanceDate) return;
            const bucket = buckets[moment(row.AttendanceDate).format("YYYY-MM-DD")];
            if (!bucket) return;
            const category = categoryOf(row);
            if (category) bucket[category] += 1;
        });

        const pick = (category) =>
            days.map((d) => buckets[d.format("YYYY-MM-DD")][category]);

        return {
            // Weekday names read well for a week; longer windows need the date.
            labels: days.map((d) =>
                trendDays <= 7 ? d.format("ddd") : d.format("DD MMM")
            ),
            series: {
                Present: pick("Present"),
                OnDuty: pick("OnDuty"),
                Leave: pick("Leave"),
                Absent: pick("Absent"),
            },
        };
    }, [attendanceList, dateRange.end, trendDays]);

    return (
        <div className="emp-attendance-page">
            {/* Header */}
            <div className="emp-attendance-header">
                <div>
                    <h2> Employee Attendance</h2>
                </div>
            </div>
            <div className="dash-actions">
                <DateRangeFilter
                    startDate={dateRange.start}
                    endDate={dateRange.end}
                    onChange={handleDateChange}
                />
            </div>
            {/* Cards */}
            <div className="emp-attendance-top">
                <div className="emp-attendance-cards">

                    <AttendanceCard
                        title="Present"
                        count={present?.TotalCount ?? 0}
                        delta={present?.DeltaCount ?? 0}
                        direction={present?.Direction ?? "same"}
                        // bg="#DCFCE7"
                        // badge="#16A34A"
                        bg="#F0FDF4"
                        border="#22C55E"
                        badge="#16A34A"
                        iconBg="#DCFCE7"
                        footerItems={[
                            {
                                label: "On Time",
                                value: present?.OnTimeCount ?? 0,
                            },
                            {
                                label: "Late",
                                value: present?.LateCount ?? 0,
                            },
                        ]}
                        icon={<i className="bi bi-person-check-fill"></i>}
                    />

                    <AttendanceCard
                        title="On Duty"
                        count={onDuty?.TotalCount ?? 0}
                        delta={onDuty?.DeltaCount ?? 0}
                        direction={onDuty?.Direction ?? "same"}
                        // bg="#DBEAFE"
                        // badge="#2563EB"
                        bg="#EFF6FF"
                        border="#2563EB"
                        badge="#2563EB"
                        iconBg="#DBEAFE"
                        footerItems={[
                            {
                                label: "Full Day",
                                value: onDuty?.FullDayCount ?? 0,
                            },
                            {
                                label: "Half Day",
                                value: onDuty?.HalfDayCount ?? 0,
                            },
                        ]}
                        icon={<i className="bi bi-briefcase-fill"></i>}
                    />

                    <AttendanceCard
                        title="Absent"
                        count={absent?.TotalCount ?? 0}
                        delta={absent?.DeltaCount ?? 0}
                        direction={absent?.Direction ?? "same"}
                        // bg="#FEE2E2"
                        // badge="#DC2626"
                        bg="#FEF2F2"
                        border="#EF4444"
                        badge="#DC2626"
                        iconBg="#FEE2E2"
                        footerItems={[
                            {
                                label: "Full Day",
                                value: absent?.FullDayCount ?? 0,
                            },
                            {
                                label: "Half Day",
                                value: absent?.HalfDayCount ?? 0,
                            },
                        ]}
                        icon={<i className="bi bi-person-x-fill"></i>}
                    />
                    <AttendanceCard
                        title="Leave"
                        count={leave?.TotalCount ?? 0}
                        delta={leave?.DeltaCount ?? 0}
                        direction={leave?.Direction ?? "same"}
                        // bg="#FEF3C7"
                        // badge="#F59E0B"
                        bg="#FFF7ED"
                        border="#F97316"
                        badge="#EA580C"
                        iconBg="#FFEDD5"
                        footerItems={[
                            {
                                label: "Full Day",
                                value: leave?.FullDayCount ?? 0,
                            },
                            {
                                label: "Half Day",
                                value: leave?.HalfDayCount ?? 0,
                            },
                        ]}
                        icon={<i className="bi bi-calendar2-x-fill"></i>}
                    />
                </div>

                <div className="status-card-wrapper">
                    <AttendanceTrendChart
                        title={trendDays === 7 ? "Weekly Insights" : "Attendance Insights"}
                        labels={attendanceTrend.labels}
                        series={attendanceTrend.series}
                        days={trendDays}
                        onDaysChange={setTrendDays}
                    />
                </div>
            </div>
            {/* Filters */}
            <div className="emp-attendance-filter">

                <div className="filter-left">

                    <DateRangeFilter

                        startDate={dateRange.start}
                        endDate={dateRange.end}
                        onChange={handleDateChange}
                    />

                    {/* <input
                        type="time"
                        className="filter-input"
                    /> */}

                    {/* <input
                        type="text"
                        placeholder="Employee Name"
                        className="filter-input"
                    />

                    <SkSelect
                        width={170}
                        value={statusFilter}
                        options={STATUS_FILTER_OPTIONS}
                        onChange={setStatusFilter}
                        ariaLabel="Filter by status"
                    /> */}

                </div>

                <div className="filter-right">

                    <button
                        className="btn btn-success d-flex align-items-center gap-2"
                        onClick={handleGenerate}
                    >
                        <i className="bi bi-gear-fill"></i>
                        Generate
                    </button>

                    {/* <button className="btn btn-primary shadow-sm w-100 fw-bold d-flex align-items-center justify-content-center gap-2">
                        <i className="bi bi-arrow-clockwise"></i>

                    </button> */}

                    <button className="btn btn-primary shadow-sm w-100 fw-bold d-flex align-items-center justify-content-center gap-2" onClick={handleExportClick} style={{ height: "40px" }}>
                        <i className="bi bi-box-arrow-up"></i> 
                    </button>

                </div>

            </div>
            {/* Table */}
            <div className="emp-attendance-table">

                {
                    loading ? (
                        <Loader inline rows={8} cols={9} />
                    ) : (
                        <div className="table-containers">
                            <ReportTable
                                data={attendanceData}
                                columns={columns}
                                stickyColumns={["sno","userName"]}
                                serverSide={true}
                                onHeaderFilterChange={handleHeaderFilterChange}
                                onDataChange={handleTableDataChange}
                                onVisibleColumnsChange={handleVisibleColumnsChange}
                                onFilterChange={handleTableFilterChange}
                            />
                        </div>
                    )

                }
            </div>


            {/* Pagination */}
            <Pagination
                currentPage={currentPage}
                totalCount={attendanceMeta?.totalRecords ?? 0}
                pageSize={pageSize}
                onPageChange={setCurrentPage}
                onPageSizeChange={(size) => {
                    setPageSize(size);
                    setCurrentPage(1);
                }}
            />
            <DownloadProgressModal
                isOpen={isDownloading}
                progress={downloadProgress}
                fileName="Attendance Report.pdf"
                onClose={() => setIsDownloading(false)}
            />
        </div>
    );
};
export default Attendance;